import { DEFAULT_SOCIAL_CASE_FILE_TAB } from "../domain/socialCaseFileTabs";
import { DEFAULT_EVALUATION_STEP } from "../domain/evaluationSteps";
// Lazy-loaded to keep the initial bundle small (same pattern as other modules).
const ReceptionView = () => import("../presentation/views/ReceptionView.vue");
const PhonePlatformView = () => import("../presentation/views/PhonePlatformView.vue");
const SocialHomeView = () => import("../presentation/views/SocialHomeView.vue");
const AppointmentPlanningView = () => import("../presentation/views/AppointmentPlanningView.vue");
const ResidentRecordView = () => import("../presentation/views/ResidentRecordView.vue");
const ResidentListView = () => import("../presentation/views/ResidentListView.vue");
const SocialCaseFileListView = () => import("../presentation/views/SocialCaseFileListView.vue");
const SocialCaseFileView = () => import("../presentation/views/SocialCaseFileView.vue");
const SocialCaseFileTabOutlet = () => import("../presentation/components/social-case-file/SocialCaseFileTabOutlet.vue");
const EvaluationWizardView = () => import("../presentation/views/EvaluationWizardView.vue");
const EvaluationStepOutlet = () => import("../presentation/components/evaluation/EvaluationStepOutlet.vue");
export const receptionRoutes = [
    {
        path: "/reception",
        name: "Reception",
        component: ReceptionView,
        meta: { requiresAuth: true, titleKey: "reception.pageTitle" },
    },
    {
        // "phone-platform" is the functional name, not a service name — public
        // paths must never leak internal service names (R83).
        path: "/reception/phone-platform",
        name: "ReceptionPhonePlatform",
        component: PhonePlatformView,
        meta: {
            requiresAuth: true,
            titleKey: "reception.phonePlatform.pageTitle",
            // Only the OpsolPhoneOperator persona carries this capability (BFF
            // PermissionHelper) — the AA is redirected by the global capability guard.
            requiresCapability: "opsol.phone-platform",
        },
    },
    {
        // Reached via useAppointmentPlanningHandoffStore + router.push from CreateEncounterModal
        // (AA and phone-operator flows both use that one modal) — never a direct nav target from
        // any menu.
        path: "/reception/appointment-planning",
        name: "ReceptionAppointmentPlanning",
        component: AppointmentPlanningView,
        meta: { requiresAuth: true, titleKey: "reception.appointmentPlanning.pageTitle" },
    },
    {
        // Entry point offering the two reception workspaces. The capability both
        // keeps non-OPSOL users out (global guard → /error/403) and makes the
        // guard await the profile, so the capability-filtered cards never render
        // against an unloaded profile.
        path: "/social-home",
        name: "SocialHome",
        component: SocialHomeView,
        meta: {
            requiresAuth: true,
            titleKey: "reception.socialHome.pageTitle",
            requiresCapability: "opsol.read",
        },
    },
    {
        // Slice 1 (OP-7946): reuses the reception module's shell and the fiche route below it. Placed
        // before the dynamic `:contactId` route for readability — vue-router ranks a static segment
        // over a dynamic one regardless of registration order, so this is not load-bearing.
        path: "/reception/usagers",
        name: "ReceptionResidentList",
        component: ResidentListView,
        meta: {
            requiresAuth: true,
            titleKey: "reception.residentList.title",
            requiresCapability: "opsol.read",
            scrollsAsPageOnShortViewport: true,
        },
    },
    {
        // Entry point: the "Consulter la fiche usager" footer action of AppointmentDetailDrawer.
        // Same reader capability as SocialHome — any OPSOL role may consult the record.
        path: "/reception/usagers/:contactId",
        name: "ReceptionResidentRecord",
        component: ResidentRecordView,
        meta: {
            requiresAuth: true,
            titleKey: "reception.residentRecord.pageTitle",
            requiresCapability: "opsol.read",
        },
    },
    {
        // Same capability as its sidebar entry (MainSidebar `socialCaseFilesEntry`).
        path: "/reception/dossiers-sociaux",
        name: "ReceptionSocialCaseFileList",
        component: SocialCaseFileListView,
        meta: {
            requiresAuth: true,
            titleKey: "reception.socialCaseFileList.title",
            requiresCapability: "opsol.read",
            scrollsAsPageOnShortViewport: true,
        },
    },
    {
        // The list's row link targets this name.
        path: "/reception/dossiers-sociaux/:id",
        name: "ReceptionSocialCaseFile",
        component: SocialCaseFileView,
        meta: {
            requiresAuth: true,
            titleKey: "reception.socialCaseFile.pageTitle",
            requiresCapability: "opsol.read",
        },
        // A function: a redirect's `params` replace the source's wholesale, so a static `params: { tab }`
        // would lose `id`.
        redirect: (to) => ({
            name: "ReceptionSocialCaseFileTab",
            params: { ...to.params, tab: DEFAULT_SOCIAL_CASE_FILE_TAB },
        }),
        children: [
            {
                path: ":tab",
                name: "ReceptionSocialCaseFileTab",
                component: SocialCaseFileTabOutlet,
            },
        ],
    },
    {
        // Reached from the appointment card's start / resume action, and from the file's Évaluations
        // tab with `?from=file` (EVALUATION_ORIGIN_FILE), which the redirect keeps. The steps are
        // children of this one record so a step change stays inside it: the wizard's leave guard only
        // sees leaving the wizard.
        path: "/reception/evaluations/:id",
        name: "ReceptionEvaluation",
        component: EvaluationWizardView,
        meta: {
            requiresAuth: true,
            titleKey: "reception.evaluation.pageTitle",
            requiresCapability: "opsol.read",
        },
        // A function, as for the file route above: a static `params` would lose `id`.
        redirect: (to) => ({
            name: "ReceptionEvaluationStep",
            params: { ...to.params, step: DEFAULT_EVALUATION_STEP },
        }),
        children: [
            {
                path: ":step",
                name: "ReceptionEvaluationStep",
                component: EvaluationStepOutlet,
            },
        ],
    },
];
// The module contributes no navigation items: its sidebar entries (`usagersEntry`, `socialCaseFilesEntry`) are
// declared in MainSidebar. /reception stays direct-URL-only until OP-6050/OP-6322 wire it as the role-based default
// landing page, and /social-home until OP-6894 lands.
export const receptionNavigationItems = [];
